// src/components/buyer-leads/main-buyer-leads.jsx
// Buyers pipeline — the "blend" view (funnel strip + needs-attention band +
// stage-grouped rows) shared with the Listing pipeline via PipelineBlendView.
import React, { useEffect, useMemo, useState } from "react";
import { Plus, Search, X } from "lucide-react";
import PageHeader from "../PageHeader";
import { toast } from "react-toastify";
import { useStateValue } from "../../context/StateProvider";
import { actionType } from "../../context/reducer";
import {
  listenBuyerLeads,
  updateBuyerLeadStage,
} from "../../utils/firebasefunctions";
import {
  BUYER_STAGE_ORDER,
  getBuyerStageColor,
  normalizeStage,
  TIMELINE_LABELS,
} from "../../utils/buyerLeadStages";
import { excludeTestAgents } from "../../utils/testAgents";
import { clientNames } from "../../utils/leadClients";
import { isOnLead } from "../../utils/leadOwnership";
import NewBuyerLeadModal from "./NewBuyerLeadModal";
import BuyerLeadDrawer from "./BuyerLeadDrawer";
import PipelineBlendView from "../pipeline/PipelineBlendView";
import { usePersistentAgentFilter } from "../../hooks/usePersistentAgentFilter";

export default function MainBuyerLeads() {
  const [{ user, buyerLeads, employees }, dispatch] = useStateValue();
  const [showLost, setShowLost] = useState(false);
  const [showWon, setShowWon] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  // "Viewing: <agent>" filter — shared hook: defaults to My Leads, persists per
  // user across visits. Switch to "All Agents" or another agent to look around.
  const [viewAgent, setViewAgent] = usePersistentAgentFilter(user?.id, "buyer");
  const [query, setQuery] = useState("");

  useEffect(() => {
    const unsub = listenBuyerLeads((rows) => {
      dispatch({ type: actionType.STORE_BUYER_LEADS, payload: rows });
    });
    return () => unsub && unsub();
  }, [dispatch]);

  // Normalize legacy stage names on read so renamed stages still group
  // correctly (Contacted → Nurturing, Offer Out → Offer Submitted).
  const leads = useMemo(
    () => (buyerLeads || []).map((l) => ({ ...l, stage: normalizeStage(l.stage) })),
    [buyerLeads],
  );

  const isPrivileged =
    user?.role === "Admin" || user?.role === "Supervisor" || user?.role === "Accountant";

  // Agents for the "Viewing" dropdown, with the current user guaranteed to be
  // present (and listed first) so "default to me" always has a matching option.
  const agentOptions = useMemo(() => {
    const list = excludeTestAgents(employees || []);
    if (user?.id && !list.some((e) => e.id === user.id)) {
      return [
        { id: user.id, firstname: user.firstname, lastname: user.lastname, email: user.email },
        ...list,
      ];
    }
    return list;
  }, [employees, user]);


  const canManage = (lead) =>
    user?.role === "Admin" ||
    user?.role === "Supervisor" ||
    isOnLead(lead, user?.id, { ownerlessFallback: true });

  // Board-only search across the fields shown on a card: name, areas they're
  // shopping, price, property type, timeline, and the assigned agent.
  const searchTokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const matchesQuery = (l) => {
    if (searchTokens.length === 0) return true;
    const haystack = [
      clientNames(l),
      l.contactName,
      ...(Array.isArray(l.areas) ? l.areas : []),
      ...(Array.isArray(l.propertyTypes) ? l.propertyTypes : []),
      l.timeline,
      l.assignedAgentName,
      l.priceMin,
      l.priceMax,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return searchTokens.every((t) => haystack.includes(t));
  };

  const visibleLeads = useMemo(
    () =>
      leads.filter((l) => {
        // Won/converted buyers leave the active pipeline; "Show Won" brings
        // them back as a fully-converted tracker (dimmed, WON ✓ badge).
        if (l.status === "converted" && !showWon) return false;
        if (!showLost && l.status === "lost") return false;
        if (!matchesQuery(l)) return false;
        if (isPrivileged) {
          if (viewAgent === "all") return true;
          return isOnLead(l, viewAgent);
        }
        // Agents' own board: ownerless leads they entered stay visible to
        // them (they have no "All Agents" view to find them in).
        return isOnLead(l, user?.id, { ownerlessFallback: true });
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [leads, showLost, showWon, isPrivileged, user?.id, viewAgent, query],
  );

  // Fully-converted buyers (won), for the tracker count in the toolbar.
  const wonCount = useMemo(
    () => leads.filter((l) => l.status === "converted").length,
    [leads],
  );

  // Stage moves happen via the row dropdown (and the drawer).
  const moveStage = async (lead, newStage) => {
    if (!lead || !newStage || newStage === lead.stage) return;
    if (!canManage(lead)) {
      toast.error("You can only move your own leads");
      return;
    }
    dispatch({
      type: actionType.STORE_BUYER_LEADS,
      payload: leads.map((l) => (l.id === lead.id ? { ...l, stage: newStage } : l)),
    });
    const res = await updateBuyerLeadStage(lead.id, newStage);
    if (!res.success) toast.error("Failed to move lead");
  };

  const selectedLead = leads.find((l) => l.id === selectedId) || null;

  // Middle column of a row: what they're shopping for, at a glance.
  const fmtK = (v) => {
    const n = Number(String(v ?? "").replace(/[^0-9.]/g, ""));
    return n > 0 ? `$${n.toLocaleString("en-US")}` : "";
  };
  const secondaryLine = (l) => {
    const price =
      fmtK(l.priceMin) && fmtK(l.priceMax)
        ? `${fmtK(l.priceMin)}–${fmtK(l.priceMax)}`
        : fmtK(l.priceMax) || fmtK(l.priceMin);
    return [
      price,
      Array.isArray(l.areas) && l.areas.length ? l.areas.slice(0, 2).join(", ") : "",
      TIMELINE_LABELS[l.timeline] || "",
    ]
      .filter(Boolean)
      .join(" · ");
  };

  return (
    <div className="flex flex-col h-[calc(100vh-120px)]">
      <PageHeader
        title="Buyers Pipeline"
        actions={
          <>
            {isPrivileged && (
              <select value={viewAgent} onChange={(e) => setViewAgent(e.target.value)}
                className="text-sm border border-slate-200 rounded-lg px-2 py-1.5 bg-white text-slate-700">
                <option value="all">👁 Viewing: All Agents</option>
                {agentOptions.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {([emp.firstname, emp.lastname].filter(Boolean).join(" ") || emp.email) +
                      (emp.id === user?.id ? " (Me)" : "")}
                  </option>
                ))}
              </select>
            )}
            <label className="text-sm text-slate-600 flex items-center gap-1.5">
              <input type="checkbox" checked={showLost} onChange={(e) => setShowLost(e.target.checked)} />
              Show Lost
            </label>
            <label className="text-sm text-slate-600 flex items-center gap-1.5">
              <input type="checkbox" checked={showWon} onChange={(e) => setShowWon(e.target.checked)} />
              Show Won
              {wonCount > 0 && (
                <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-100 rounded-full px-1.5 py-0.5">
                  {wonCount}
                </span>
              )}
            </label>
            <button onClick={() => setAddOpen(true)}
              className="inline-flex items-center gap-1.5 bg-[#1E293B] hover:opacity-90 text-white rounded-full px-4 py-2 text-sm font-semibold">
              <Plus size={16} /> Add Buyer Lead
            </button>
          </>
        }
      />

      {/* Search — its own row beneath the header (buyer pipeline only). */}
      <div className="px-3 pt-3 pb-1">
        <div className="relative w-full max-w-md">
          <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search buyers — name, area, price…"
            className="w-full text-sm border border-slate-200 rounded-lg pl-8 pr-7 py-1.5 bg-white text-slate-700 placeholder:text-slate-400"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              title="Clear search"
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-3 pb-3">
        <PipelineBlendView
          leads={visibleLeads}
          stages={BUYER_STAGE_ORDER}
          parkingStages={[]}
          stageDot={(stage) => (
            <span
              className={`${getBuyerStageColor(stage)} rounded-full size-2.5 inline-block flex-shrink-0`}
            />
          )}
          wonLabel="Won · Fully Converted"
          apptLabel="Appt"
          renderSecondary={secondaryLine}
          canManage={canManage}
          onOpen={(lead) => setSelectedId(lead.id)}
          onStageChange={moveStage}
        />
      </div>

      {addOpen && <NewBuyerLeadModal onClose={() => setAddOpen(false)} />}
      {selectedLead && (
        <BuyerLeadDrawer
          lead={selectedLead}
          canManage={canManage(selectedLead)}
          onClose={() => setSelectedId(null)}
        />
      )}
    </div>
  );
}
